import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import {
  createQuery,
  createQueryCategory,
  deleteQueryCategory,
  getAllQueries,
  getQueryCategories,
  serveQueryToEmployees,
  deleteQuery,
  updateQueryCategory,
} from "../../api/queries";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { InviteModal, DeleteItem, SuccessModal } from "../../components/modal";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { FaPlus, FaEye, FaTrash } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import { toast } from "react-toastify";

const QUERY_STATUSES = ["Pending", "Active", "In Progress", "Resolved", "Closed"];
const QUERY_PRIORITIES = ["Low", "Medium", "High", "Critical"];
const DISCIPLINARY_TYPES = [
  { value: "QUERY", label: "Query" },
  { value: "WARNING", label: "Warning" },
  { value: "SUSPENSION", label: "Suspension" },
];
const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getLastServedAt = (assignments = []) =>
  assignments.reduce((latest, assignment) => {
    const assignedAt = assignment.assignedAt ? new Date(assignment.assignedAt).getTime() : 0;
    return assignedAt > latest ? assignedAt : latest;
  }, 0);

const AdminQueries = () => {
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();

  const [filteredQueries, setFilteredQueries] = useState([]);
  const [disciplinaryCounts, setDisciplinaryCounts] = useState({ QUERY: 0, WARNING: 0, SUSPENSION: 0 });
  const [queryCategories, setQueryCategories] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingQuery, setIsCreatingQuery] = useState(false);
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [isServingQuery, setIsServingQuery] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isServeModalOpen, setIsServeModalOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [newCategory, setNewCategory] = useState({ name: "", description: "" });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategory, setEditingCategory] = useState({ name: "", description: "", isActive: true });

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Create query modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newQuery, setNewQuery] = useState({
    title: "",
    description: "",
    category: "",
    priority: "Medium",
    expectedResolutionDays: 7,
    disciplinaryType: "QUERY",
    effectiveStartDate: "",
    effectiveEndDate: "",
  });

  // Selected query for actions
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedQueryForDelete, setSelectedQueryForDelete] = useState(null);
  const activeQueryCategories = queryCategories.filter((category) => category.isActive !== false);

  // Load queries
  const loadQueries = useCallback(async () => {
    try {
      setIsLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (priorityFilter) filters.priority = priorityFilter;
      if (categoryFilter) filters.category = categoryFilter;
      const response = await getAllQueries(filters);
      const records = response.queries || response.data || [];
      setDisciplinaryCounts(records.reduce((counts, record) => ({ ...counts, [record.disciplinaryType || "QUERY"]: (counts[record.disciplinaryType || "QUERY"] || 0) + 1 }), { QUERY: 0, WARNING: 0, SUSPENSION: 0 }));
      setFilteredQueries(typeFilter ? records.filter((record) => (record.disciplinaryType || "QUERY") === typeFilter) : records);
    } catch (error) {
      toast.error("Failed to load queries");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, priorityFilter, categoryFilter, typeFilter]);

  // Load available employees
  const loadEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/organizations/employees?status=approved`);
      setAvailableEmployees(response.data.employees || []);
    } catch (error) {
      toast.error("Failed to load employees");
      console.error("Failed to load employees:", error);
    }
  }, [apiBaseUrl]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await getQueryCategories({ includeInactive: true });
      setQueryCategories(response.categories || response.data || []);
    } catch (error) {
      toast.error("Failed to load query categories");
      console.error(error);
    }
  }, []);

  useEffect(() => {
    loadQueries();
    loadEmployees();
    loadCategories();
  }, [loadQueries, loadEmployees, loadCategories]);

  // Create new query
  const handleCreateQuery = async (e) => {
    e.preventDefault();
    if (!newQuery.title || !newQuery.description || !newQuery.category) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (newQuery.disciplinaryType === "SUSPENSION" && (!newQuery.effectiveStartDate || !newQuery.effectiveEndDate)) {
      toast.error("Select the suspension start and end dates");
      return;
    }

    try {
      setIsCreatingQuery(true);
      await createQuery(newQuery);
      setSuccessMessage(`${DISCIPLINARY_TYPES.find((item) => item.value === newQuery.disciplinaryType)?.label || "Record"} created successfully!`);
      setIsSuccessOpen(true);
      setIsCreateModalOpen(false);
      setNewQuery({
        title: "",
        description: "",
        category: "",
        priority: "Medium",
        expectedResolutionDays: 7,
        disciplinaryType: "QUERY",
        effectiveStartDate: "",
        effectiveEndDate: "",
      });
      await loadQueries();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create query");
    } finally {
      setIsCreatingQuery(false);
    }
  };

  // Serve query to employees
  const handleServeQuery = async () => {
    if (!selectedQuery || selectedEmployees.length === 0) {
      toast.error("Please select employees to serve the query");
      return;
    }

    try {
      setIsServingQuery(true);
      await serveQueryToEmployees(selectedQuery.id, selectedEmployees);
      setSuccessMessage(
        `Query served to ${selectedEmployees.length} employee(s)!`
      );
      setIsSuccessOpen(true);
      setIsServeModalOpen(false);
      setSelectedEmployees([]);
      setSelectedQuery(null);
      await loadQueries();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to serve query");
    } finally {
      setIsServingQuery(false);
    }
  };

  // Delete query
  const handleDeleteQuery = async () => {
    if (!selectedQueryForDelete) return;

    try {
      await deleteQuery(selectedQueryForDelete.id);
      setSuccessMessage("Query deleted successfully!");
      setIsSuccessOpen(true);
      setIsDeleteModalOpen(false);
      setSelectedQueryForDelete(null);
      await loadQueries();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete query");
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();

    if (!newCategory.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      setIsSavingCategory(true);
      await createQueryCategory({
        name: newCategory.name.trim(),
        description: newCategory.description.trim(),
      });
      setNewCategory({ name: "", description: "" });
      toast.success("Query category created");
      await loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create category");
    } finally {
      setIsSavingCategory(false);
    }
  };

  const startEditingCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditingCategory({
      name: category.name || "",
      description: category.description || "",
      isActive: category.isActive ?? true,
    });
  };

  const handleUpdateCategory = async (categoryId) => {
    if (!editingCategory.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      setIsSavingCategory(true);
      await updateQueryCategory(categoryId, {
        name: editingCategory.name.trim(),
        description: editingCategory.description.trim(),
        isActive: editingCategory.isActive,
      });
      setEditingCategoryId(null);
      toast.success("Query category updated");
      await Promise.all([loadCategories(), loadQueries()]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update category");
    } finally {
      setIsSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      setIsSavingCategory(true);
      await deleteQueryCategory(categoryId);
      toast.success("Query category deleted");
      await loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete category");
    } finally {
      setIsSavingCategory(false);
    }
  };

  const openServeModal = async (query) => {
    setSelectedQuery(query);
    setSelectedEmployees([]);
    setIsServeModalOpen(true);
    await loadEmployees();
  };

  const openDeleteModal = (query) => {
    setSelectedQueryForDelete(query);
    setIsDeleteModalOpen(true);
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: "bg-green-100 text-green-800",
      Medium: "bg-yellow-100 text-yellow-800",
      High: "bg-orange-100 text-orange-800",
      Critical: "bg-red-100 text-red-800",
    };
    return colors[priority] || colors.Medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: "bg-gray-100 text-gray-800",
      Active: "bg-blue-100 text-blue-800",
      "In Progress": "bg-purple-100 text-purple-800",
      Resolved: "bg-green-100 text-green-800",
      Closed: "bg-gray-500 text-white",
    };
    return colors[status] || colors.Pending;
  };

  if (isLoading) {
    return <FetchLoadingState message="Loading queries..." />;
  }

  return (
    <div className="dashboard-enter">
      <PageIntroNav
        breadcrumbs={[
          { label: "Query Management", path: "/query-management" },
          { label: "Admin View" },
        ]}
        title="Disciplinary Status"
        subtitle="Manage employee queries, formal warnings, suspensions, responses, and resolution history."
        fallbackPath="/query-management"
        backLabel="Back to Query Management"
      />

      <div className="mb-6 flex flex-wrap items-center gap-3 dashboard-enter">
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-dashed border-[#1234CD] bg-white px-4 py-2 text-sm font-medium text-[#1234CD] hover:bg-[#EEF2FF] dashboard-button-hover"
        >
          <FaPlus size={12} />
          Create disciplinary record
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-3 dashboard-enter dashboard-enter-delay-1">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm smooth-transition">
          <option value="">All record types</option>
          {DISCIPLINARY_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm smooth-transition"
        >
          <option value="">All Statuses</option>
          {QUERY_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm smooth-transition"
        >
          <option value="">All Priorities</option>
          {QUERY_PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {priority}
            </option>
          ))}
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm smooth-transition"
        >
          <option value="">All Categories</option>
          {activeQueryCategories.map((category) => (
            <option key={category.id || category.name} value={category.name}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        {DISCIPLINARY_TYPES.map((type) => {
          const count = disciplinaryCounts[type.value] || 0;
          return (
            <button key={type.value} type="button" onClick={() => setTypeFilter(typeFilter === type.value ? "" : type.value)} className={`rounded-xl border p-4 text-left shadow-sm transition ${typeFilter === type.value ? "border-[#1234CD] bg-[#EEF3FF]" : "border-slate-100 bg-white"}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{type.label}s</p>
              <p className="mt-2 text-2xl font-bold text-[#071C50]">{count}</p>
            </button>
          );
        })}
      </div>

      <div className="mb-6 rounded-xl border border-gray-100 bg-white p-5 shadow-sm dashboard-enter dashboard-enter-delay-1">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-gray-900">Query Categories</p>
            <p className="text-sm text-gray-500">Create, edit, deactivate, or delete unused categories.</p>
          </div>
        </div>

        <form onSubmit={handleCreateCategory} className="mb-4 grid gap-3 md:grid-cols-[1fr_1.5fr_auto]">
          <input
            type="text"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Category name"
          />
          <input
            type="text"
            value={newCategory.description}
            onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Description"
          />
          <button
            type="submit"
            disabled={isSavingCategory}
            className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            Add Category
          </button>
        </form>

        <div className="divide-y divide-gray-100">
          {queryCategories.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">No query categories yet.</p>
          ) : (
            queryCategories.map((category) => {
              const isEditing = editingCategoryId === category.id;

              return (
                <div key={category.id} className="grid gap-3 py-3 md:grid-cols-[1fr_1.5fr_auto] md:items-center">
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editingCategory.name}
                        onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        value={editingCategory.description}
                        onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={editingCategory.isActive}
                            onChange={(e) => setEditingCategory({ ...editingCategory, isActive: e.target.checked })}
                          />
                          Active
                        </label>
                        <button
                          type="button"
                          onClick={() => handleUpdateCategory(category.id)}
                          disabled={isSavingCategory}
                          className="rounded-lg bg-[#1234CD] px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingCategoryId(null)}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="font-medium text-gray-900">{category.name}</p>
                        <p className="text-xs text-gray-500">{category.isActive ? "Active" : "Inactive"}</p>
                      </div>
                      <p className="text-sm text-gray-600">{category.description || "-"}</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => startEditingCategory(category)}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCategory(category.id)}
                          disabled={isSavingCategory}
                          className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600 disabled:opacity-60"
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Queries Table */}
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dashboard-enter dashboard-enter-delay-2 dashboard-card-hover">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-4 text-sm font-medium text-gray-700">
                  Title
                </th>
                <th className="px-6 py-4 text-sm font-medium text-gray-700">
                  Category
                </th>
                <th className="px-6 py-4 text-sm font-medium text-gray-700">Type</th>
                <th className="px-6 py-4 text-sm font-medium text-gray-700">
                  Priority
                </th>
                <th className="px-6 py-4 text-sm font-medium text-gray-700">
                  Status
                </th>
                <th className="px-6 py-4 text-sm font-medium text-gray-700">
                  Served history
                </th>
                <th className="px-6 py-4 text-sm font-medium text-gray-700">
                  Created
                </th>
                <th className="px-6 py-4 text-sm font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredQueries.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No disciplinary records found. Create one to get started.
                  </td>
                </tr>
              ) : (
                filteredQueries.map((query) => {
                  const assignments = query.assignments || [];
                  const respondedCount = assignments.filter((assignment) => assignment.hasResponded).length;
                  const lastServedAt = getLastServedAt(assignments);

                  return (
                    <tr
                      key={query.id}
                      className="table-row-enter border-b border-gray-100 hover:bg-gray-50 transition smooth-transition"
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{query.title}</p>
                        <p className="text-sm text-gray-500 line-clamp-1">
                          {query.description}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {query.category}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${(query.disciplinaryType || "QUERY") === "SUSPENSION" ? "bg-rose-100 text-rose-700" : query.disciplinaryType === "WARNING" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                          {DISCIPLINARY_TYPES.find((item) => item.value === (query.disciplinaryType || "QUERY"))?.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                            query.priority
                          )}`}
                        >
                          {query.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-medium smooth-transition ${getStatusColor(
                            query.status
                          )}`}
                        >
                          {query.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <p className="font-medium text-gray-900">{assignments.length} affected</p>
                        <p className="text-xs text-gray-500">{respondedCount} responded</p>
                        <p className="text-xs text-gray-500">Last served: {formatDateTime(lastServedAt)}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(query.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openServeModal(query)}
                            className="rounded-lg bg-blue-50 p-2 text-blue-600 hover:bg-blue-100 transition dashboard-button-hover"
                            title="Serve to employees"
                          >
                            <FiSend size={14} />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/query-management/query-details/${query.id}`)
                            }
                            className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 transition dashboard-button-hover"
                            title="View served history"
                          >
                            <FaEye size={14} />
                          </button>
                          <button
                            onClick={() => openDeleteModal(query)}
                            className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100 transition dashboard-button-hover"
                            title="Delete query"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Query Modal */}
      <InviteModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Create disciplinary record</p>
          <p className="mt-2 text-sm text-gray-600">
            Create a query, formal warning, or suspension record to serve to employees.
          </p>

          <form className="my-8 space-y-4" onSubmit={handleCreateQuery}>
            <div>
              <label className="mb-2 block text-sm font-medium text-black">Record type</label>
              <select value={newQuery.disciplinaryType} onChange={(e) => setNewQuery({ ...newQuery, disciplinaryType: e.target.value, effectiveStartDate: "", effectiveEndDate: "" })} className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]">
                {DISCIPLINARY_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-black">
                Record title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newQuery.title}
                onChange={(e) =>
                  setNewQuery({ ...newQuery, title: e.target.value })
                }
                className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                placeholder="e.g. Salary Review Process"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-black">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={newQuery.description}
                onChange={(e) =>
                  setNewQuery({ ...newQuery, description: e.target.value })
                }
                className="min-h-32 w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                placeholder="Provide details about the query"
              />
            </div>

            {newQuery.disciplinaryType === "SUSPENSION" ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-black">Suspension starts <span className="text-red-500">*</span></label>
                  <input type="date" value={newQuery.effectiveStartDate} onChange={(e) => setNewQuery({ ...newQuery, effectiveStartDate: e.target.value })} className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-black">Suspension ends <span className="text-red-500">*</span></label>
                  <input type="date" min={newQuery.effectiveStartDate || undefined} value={newQuery.effectiveEndDate} onChange={(e) => setNewQuery({ ...newQuery, effectiveEndDate: e.target.value })} className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]" />
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-black">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={newQuery.category}
                  onChange={(e) =>
                    setNewQuery({ ...newQuery, category: e.target.value })
                  }
                  className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                >
                  <option value="">Select category</option>
                  {activeQueryCategories.map((cat) => (
                    <option key={cat.id || cat.name} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">
                  Priority
                </label>
                <select
                  value={newQuery.priority}
                  onChange={(e) =>
                    setNewQuery({ ...newQuery, priority: e.target.value })
                  }
                  className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                >
                  {QUERY_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-black">
                Expected Resolution Days
              </label>
              <input
                type="number"
                value={newQuery.expectedResolutionDays}
                onChange={(e) =>
                  setNewQuery({
                    ...newQuery,
                    expectedResolutionDays: parseInt(e.target.value),
                  })
                }
                className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                placeholder="7"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="flex-1 rounded-xl border border-[#1234CD] px-4 py-3 font-medium text-[#1234CD] dashboard-button-hover"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreatingQuery}
                className="flex-1 rounded-xl bg-[#1234CD] px-4 py-3 font-medium text-white disabled:opacity-60 dashboard-button-hover"
              >
                {isCreatingQuery ? "Creating..." : "Create record"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      {/* Serve Query Modal */}
      <InviteModal
        isOpen={isServeModalOpen}
        onClose={() => {
          setIsServeModalOpen(false);
          setSelectedEmployees([]);
        }}
      >
        <div className="my-8 font-Outfit dashboard-scale-in">
          <p className="text-2xl font-semibold text-black">
            Serve Query to Employees
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Select employees to receive this query
          </p>

          <div className="my-8 max-h-80 overflow-y-auto">
            <div className="space-y-2">
              {availableEmployees.length === 0 ? (
                <p className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500">
                  No approved employees found.
                </p>
              ) : (
                availableEmployees.map((employee, index) => (
                  <label
                    key={employee.id}
                    className={`stagger-item flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 cursor-pointer transition`}
                    style={{ "--stagger-index": index }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={() => toggleEmployeeSelection(employee.id)}
                      className="w-4 h-4 text-[#1234CD] rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {employee.firstName} {employee.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {employee.officialEmail || employee.personalEmail || employee.email || "No email"}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>

          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsServeModalOpen(false);
                setSelectedEmployees([]);
              }}
              className="flex-1 rounded-xl border border-[#1234CD] px-4 py-3 font-medium text-[#1234CD] dashboard-button-hover"
            >
              Cancel
            </button>
            <button
              onClick={handleServeQuery}
              disabled={isServingQuery || selectedEmployees.length === 0}
              className="flex-1 rounded-xl bg-[#1234CD] px-4 py-3 font-medium text-white disabled:opacity-60 dashboard-button-hover"
            >
              {isServingQuery
                ? "Serving..."
                : `Serve to ${selectedEmployees.length} Employee${
                    selectedEmployees.length !== 1 ? "s" : ""
                  }`}
            </button>
          </div>
        </div>
      </InviteModal>

      {/* Delete Modal */}
      <DeleteItem
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedQueryForDelete(null);
        }}
      >
        <div>
          <h1 className="text-center text-lg font-bold text-black">
            Delete this query?
          </h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            {selectedQueryForDelete?.title}
          </p>
          <p className="mt-1 text-center text-sm text-gray-500">
            Action cannot be undone.
          </p>
        </div>
        <div className="my-4 flex gap-4">
          <button
            onClick={() => {
              setIsDeleteModalOpen(false);
              setSelectedQueryForDelete(null);
            }}
            className="flex-1 rounded-xl border-2 border-[#1234CD] px-4 py-2 font-medium text-[#1234CD] dashboard-button-hover"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteQuery}
            className="flex-1 rounded-xl bg-red-600 px-4 py-2 font-medium text-white dashboard-button-hover"
          >
            Delete
          </button>
        </div>
      </DeleteItem>

      {/* Success Modal */}
      <SuccessModal isOpen={isSuccessOpen} onClose={() => setIsSuccessOpen(false)}>
        <div className="flex flex-col items-center gap-4">
          <p className="text-lg font-semibold text-green-600">{successMessage}</p>
        </div>
      </SuccessModal>
    </div>
  );
};

export default AdminQueries;
